import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Mic, MicOff, Send, Loader2, User, Bot, Lightbulb, Crown, History, ChevronRight, Clock, Trophy, X, Lock, Video, VideoOff, Volume2 } from 'lucide-react';
import ExerciseList from '@/components/ExerciseList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpeakingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [finalFeedback, setFinalFeedback] = useState(null);
  const [finalScore, setFinalScore] = useState(null);
  const [interactionsRemaining, setInteractionsRemaining] = useState(5);
  const [showHistory, setShowHistory] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [avatarVideoUrl, setAvatarVideoUrl] = useState(null);
  const [avatarVideoLoading, setAvatarVideoLoading] = useState(false);
  const [avatarVideoError, setAvatarVideoError] = useState(false);
  const [avatarEnabled, setAvatarEnabled] = useState(true);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [conversationFlowIndex, setConversationFlowIndex] = useState(0);
  const [isConversational, setIsConversational] = useState(false);
  const [overallFeedback, setOverallFeedback] = useState(null);
  const [overallScore, setOverallScore] = useState(null);
  const [loadingOverallFeedback, setLoadingOverallFeedback] = useState(false);
  
  // HeyGen credit status - set to false when credits are available
  const HEYGEN_DISABLED = true;
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoRef = useRef(null);
  const pollingRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchExercises();
    checkPremiumStatus();
    fetchSessionHistory();
  }, [user?.level]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const checkPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/user-status`);
      setIsPremium(response.data.is_premium);
    } catch (error) {
      console.error('Error checking premium status:', error);
    }
  };

  const fetchSessionHistory = async () => {
    try {
      const response = await axios.get(`${API}/speaking/sessions?limit=10`);
      setSessionHistory(response.data);
    } catch (error) {
      console.error('Error fetching session history:', error);
    }
  };

  const fetchExercises = async () => {
    try {
      const params = new URLSearchParams();
      params.append('exercise_type', 'speaking');
      if (user?.level) params.append('level', user.level);
      
      const response = await axios.get(`${API}/exercises?${params}`);
      setExercises(response.data);
      if (response.data.length > 0) {
        const firstAvailable = response.data.find(e => !e.is_locked) || response.data[0];
        initializeNewConversation(firstAvailable);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExercise = (exercise) => {
    if (exercise.is_locked) {
      toast.error('Este ejercicio requiere Premium');
      return;
    }
    initializeNewConversation(exercise);
  };

  // Generate and play TTS audio for examiner's message
  const playExaminerAudio = useCallback(async (text, voiceId) => {
    if (!avatarEnabled) return;
    
    setAudioLoading(true);
    
    try {
      const payload = { text: text.substring(0, 500) };
      if (voiceId) payload.voice_id = voiceId;
      
      const response = await axios.post(`${API}/tts/generate`, 
        payload,
        { responseType: 'blob' }
      );
      
      // Create audio URL from blob
      const audioUrl = URL.createObjectURL(response.data);
      
      // Play audio - wrapped in try-catch for autoplay policy
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        try {
          await audioRef.current.play();
          setAudioPlaying(true);
        } catch (playError) {
          // Autoplay was prevented - this is expected before user interaction
          console.log('Audio autoplay prevented - user needs to interact first');
        }
      }
      
    } catch (error) {
      console.error('Error generating audio:', error);
    } finally {
      setAudioLoading(false);
    }
  }, [avatarEnabled]);

  // Generate avatar video for examiner's message
  const generateAvatarVideo = useCallback(async (script) => {
    // Skip video generation when HeyGen is disabled (no credits)
    if (!avatarEnabled || HEYGEN_DISABLED) return;
    
    setAvatarVideoLoading(true);
    setAvatarVideoUrl(null);
    setAvatarVideoError(false);
    
    try {
      // Request video generation
      const response = await axios.post(`${API}/avatar/generate-video`, {
        script: script.substring(0, 500) // Limit script length
      });
      
      const videoId = response.data.video_id;
      
      // Poll for video completion
      const pollForVideo = async () => {
        try {
          const statusResponse = await axios.get(`${API}/avatar/video-status/${videoId}`);
          const { status } = statusResponse.data;
          
          if (status === 'completed') {
            // Use proxy URL instead of direct HeyGen URL to avoid CORS
            setAvatarVideoUrl(`${API}/avatar/proxy-video/${videoId}`);
            setAvatarVideoLoading(false);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          } else if (status === 'failed') {
            console.error('Avatar video generation failed');
            setAvatarVideoLoading(false);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        } catch (error) {
          console.error('Error checking video status:', error);
        }
      };
      
      // Start polling every 3 seconds
      pollingRef.current = setInterval(pollForVideo, 3000);
      
      // Also check immediately
      setTimeout(pollForVideo, 1000);
      
      // Stop polling after 5 minutes max
      setTimeout(() => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setAvatarVideoLoading(false);
        }
      }, 300000);
      
    } catch (error) {
      console.error('Error generating avatar video:', error);
      setAvatarVideoLoading(false);
    }
  }, [avatarEnabled]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const initializeNewConversation = (exercise) => {
    setCurrentExercise(exercise);
    setSessionId(null);
    setSessionEnded(false);
    setFinalFeedback(null);
    setFinalScore(null);
    setInteractionsRemaining(5);
    setAvatarVideoUrl(null);
    setAvatarVideoLoading(false);
    setAvatarVideoError(false);
    setConversationFlowIndex(0);
    
    // Check if this exercise uses the new conversational flow
    const hasConversationalFlow = exercise.content?.is_conversational && 
                                   exercise.content?.conversation_flow?.length > 0;
    setIsConversational(hasConversationalFlow);
    
    let starterMessage;
    if (hasConversationalFlow) {
      // Use the first prompt from the conversation flow
      starterMessage = exercise.content.conversation_flow[0].examiner_text;
    } else {
      // Fallback to the old format
      starterMessage = `Hello! I'm your Cambridge English speaking examiner. Today we'll practice "${exercise.content?.topic || 'conversation'}". ${exercise.content?.starter_prompts?.[0] || 'Tell me about yourself.'}`;
    }
    
    setMessages([{
      role: 'assistant',
      content: starterMessage
    }]);
    
    // Play audio for the starter message
    if (avatarEnabled) {
      // Use pre-recorded intro audio if available, otherwise generate TTS
      if (exercise.content?.intro_audio_url) {
        setAudioLoading(true);
        if (audioRef.current) {
          audioRef.current.src = `${API.replace('/api', '')}${exercise.content.intro_audio_url}`;
          audioRef.current.play()
            .then(() => setAudioPlaying(true))
            .catch(() => console.log('Autoplay prevented for intro audio'));
          setAudioLoading(false);
        }
      } else {
        playExaminerAudio(starterMessage, exercise.content?.voice_id);
      }
    }
    
    // Generate avatar video for the starter message
    if (avatarEnabled) {
      generateAvatarVideo(starterMessage);
    }
  };

  const handleGetOverallFeedback = async () => {
    if (!currentExercise) return;
    
    setLoadingOverallFeedback(true);
    setOverallFeedback(null);
    setOverallScore(null);
    
    try {
      const response = await axios.post(`${API}/speaking/overall-feedback`, {
        sample_test: currentExercise.sample_test,
        level: currentExercise.level
      });
      
      setOverallFeedback(response.data.overall_feedback);
      setOverallScore(response.data.average_score);
      toast.success('Overall feedback generated!');
    } catch (error) {
      console.error('Error getting overall feedback:', error);
      const message = error.response?.data?.detail || 'Error generating overall feedback';
      toast.error(message);
    } finally {
      setLoadingOverallFeedback(false);
    }
  };


  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Grabando... Habla ahora');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/ai/speech-to-text`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.text) {
        setInput(response.data.text);
        toast.success('¡Transcripción completa!');
        
        if (!response.data.is_premium) {
          setDailyUsage(prev => prev + 1);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Límite diario alcanzado');
      } else {
        toast.error('Error al transcribir audio');
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !currentExercise || sending || sessionEnded) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);

    try {
      const response = await axios.post(`${API}/ai/speaking-practice`, {
        exercise_id: currentExercise.id,
        message: userMessage,
        conversation_history: newMessages.map(m => ({
          role: m.role,
          content: m.content
        })),
        session_id: sessionId
      });

      // Update session ID if this is a new session
      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };

      setMessages(prev => [...prev, assistantMessage]);
      setInteractionsRemaining(response.data.interactions_remaining);

      // Play audio for the examiner's response
      if (avatarEnabled && !response.data.is_final) {
        playExaminerAudio(response.data.response, currentExercise?.content?.voice_id);
      }

      // Generate avatar video for the examiner's response
      if (avatarEnabled && !response.data.is_final) {
        generateAvatarVideo(response.data.response);
      }

      // Check if this was the final turn
      if (response.data.is_final) {
        setSessionEnded(true);
        setFinalFeedback(response.data.feedback);
        setFinalScore(response.data.score);
        
        // Save the session
        const finalConversation = [...newMessages, assistantMessage];
        await saveSession(finalConversation, response.data.score, response.data.feedback);
        
        toast.success('¡Sesión completada! Revisa tu feedback.');
      }
    } catch (error) {
      console.error('Error in speaking practice:', error);
      toast.error('Error al obtener respuesta. Intenta de nuevo.');
      setMessages(messages);
    } finally {
      setSending(false);
    }
  };

  const saveSession = async (conversation, score, feedback) => {
    try {
      await axios.post(`${API}/speaking/sessions`, {
        exercise_id: currentExercise.id,
        conversation,
        score,
        feedback
      });
      // Refresh history
      fetchSessionHistory();
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const handleNewConversation = () => {
    if (currentExercise) {
      initializeNewConversation(currentExercise);
    }
  };

  const viewSession = async (session) => {
    setSelectedSession(session);
    setShowHistory(false);
  };

  const closeSessionView = () => {
    setSelectedSession(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando práctica de speaking...</div>
      </div>
    );
  }

  // Viewing a past session
  if (selectedSession) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <button 
                onClick={closeSessionView}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Volver</span>
              </button>
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-orange-500" />
                <span className="font-medium text-slate-900">Historial de Sesión</span>
              </div>
              <span className={`level-badge level-${selectedSession.level?.toLowerCase() || 'b2'}`}>
                {selectedSession.level || 'B2'}
              </span>
            </div>
          </div>
        </header>

        <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
          {/* Session Info */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-slate-900">{selectedSession.exercise_title}</h2>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                {formatDate(selectedSession.created_at)}
              </div>
            </div>
            {selectedSession.score !== null && (
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold text-lg">{selectedSession.score}%</span>
                </div>
                {selectedSession.feedback && (
                  <p className="text-sm text-slate-600 flex-1">{selectedSession.feedback}</p>
                )}
              </div>
            )}
          </div>

          {/* Conversation */}
          <div className="space-y-4">
            {selectedSession.conversation?.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-4 rounded-sm ${
                    message.role === 'user'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-700'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-orange-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-slate-900">Speaking Practice</span>
            </div>
            <div className="flex items-center gap-2">
              {user?.level && (
                <span className={`level-badge level-${user.level.toLowerCase()}`}>
                  {user.level}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="text-slate-600"
                data-testid="history-btn"
              >
                <History className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* History Sidebar */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowHistory(false)}>
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Historial de Sesiones</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="h-[calc(100vh-64px)]">
              <div className="p-4 space-y-3">
                {sessionHistory.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    No tienes sesiones guardadas aún.
                  </p>
                ) : (
                  sessionHistory.map((session) => (
                    <button
                      key={session.id}
                      onClick={() => viewSession(session)}
                      className="w-full text-left p-4 bg-slate-50 hover:bg-slate-100 rounded-sm transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-slate-900 truncate">
                          {session.exercise_title}
                        </span>
                        <span className={`level-badge level-${session.level?.toLowerCase() || 'b2'} text-xs`}>
                          {session.level}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{formatDate(session.created_at)}</span>
                        {session.score !== null && (
                          <span className="flex items-center gap-1 text-amber-600 font-medium">
                            <Trophy className="w-3 h-3" />
                            {session.score}%
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Exercise List - Only show if there are multiple exercises */}
      {exercises.length > 1 && (
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-4">
          <ExerciseList
            exercises={exercises}
            currentExercise={currentExercise}
            onSelectExercise={handleSelectExercise}
            completedIds={[]}
          />
        </div>
      )}

      {!currentExercise ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12">
            <Mic className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No hay ejercicios disponibles
            </h2>
            <p className="text-slate-600 mb-4">
              {user?.level 
                ? `No encontramos ejercicios de speaking para el nivel ${user.level}`
                : 'Selecciona un nivel para ver los ejercicios'}
            </p>
            <Button onClick={() => navigate('/level-select')} className="rounded-full">
              Seleccionar Nivel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6">
          {/* Exercise Info */}
          <div className="py-4 bg-white border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-semibold text-slate-900">{currentExercise.title}</h1>
                <p className="text-sm text-orange-600">{currentExercise.title_es}</p>
              </div>
            </div>
          </div>

          {/* Premium badge for voice - only show for premium-locked exercises */}
          {!isPremium && currentExercise && !currentExercise.is_free && (
            <div className="py-2 bg-amber-50 border-b border-amber-100">
              <button
                onClick={() => navigate('/subscription')}
                className="flex items-center gap-2 text-amber-700 text-sm hover:text-amber-800"
              >
                <Crown className="w-4 h-4" />
                <span>Premium subscription required for this test. <strong>Upgrade now</strong></span>
              </button>
            </div>
          )}

          {/* Hidden audio element for TTS playback */}
          <audio 
            ref={audioRef} 
            onEnded={() => setAudioPlaying(false)}
            onPlay={() => setAudioPlaying(true)}
            className="hidden"
          />

          {/* AI Avatar Video Section */}
          <div className="py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <div className="flex items-start gap-4">
              {/* Avatar Display */}
              <div className="relative w-48 h-48 bg-slate-900 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                {HEYGEN_DISABLED ? (
                  /* Static Examiner Avatar with Audio */
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {/* Examiner image */}
                    <div className="relative w-full h-full">
                      <img 
                        src={currentExercise?.content?.examiner_image || "https://customer-assets.emergentagent.com/job_571c337b-2774-4e97-895c-e2c9535c102b/artifacts/z4dgbfsu_Examiner%20Speaking%20tests.png"}
                        alt="Examiner"
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay when speaking */}
                      {(audioPlaying || audioLoading) && (
                        <div className="absolute inset-0 bg-green-500 bg-opacity-10 animate-pulse"></div>
                      )}
                      {/* Audio indicator badge */}
                      <div className={`absolute bottom-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                        audioLoading ? 'bg-yellow-500 text-white' : 
                        audioPlaying ? 'bg-green-500 text-white' : 
                        'bg-slate-800 bg-opacity-75 text-white'
                      }`}>
                        {audioLoading ? '⏳' : audioPlaying ? '🔊' : '🎙️'}
                      </div>
                    </div>
                  </div>
                ) : avatarVideoLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-2" />
                    <span className="text-xs text-slate-400">Generando avatar...</span>
                  </div>
                ) : avatarVideoUrl && !avatarVideoError ? (
                  <video
                    ref={videoRef}
                    src={avatarVideoUrl}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    onEnded={() => {}}
                    onError={(e) => {
                      console.error('Video playback error:', e);
                      setAvatarVideoError(true);
                    }}
                    onLoadedData={() => {
                      console.log('Avatar video loaded successfully');
                      setAvatarVideoError(false);
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800">
                    <Bot className="w-12 h-12 text-slate-600 mb-2" />
                    <span className="text-xs text-slate-500">Examiner Avatar</span>
                  </div>
                )}
                
                {/* Avatar toggle button */}
                <button
                  onClick={() => setAvatarEnabled(!avatarEnabled)}
                  className={`absolute top-2 right-2 p-1.5 rounded-full transition-colors ${
                    avatarEnabled ? 'bg-green-500 text-white' : 'bg-slate-600 text-slate-300'
                  }`}
                  title={avatarEnabled ? 'Audio activado' : 'Audio desactivado'}
                >
                  {avatarEnabled ? <Volume2 className="w-3 h-3" /> : <VideoOff className="w-3 h-3" />}
                </button>
                
                {/* Replay audio button */}
                {avatarEnabled && audioRef.current?.src && !audioPlaying && !audioLoading && (
                  <button
                    onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.play().catch(() => {});
                      }
                    }}
                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    title="Reproducir audio"
                  >
                    <Volume2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              
              {/* Latest Examiner Message */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Examiner</span>
                  {avatarEnabled && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      🔊 Audio AI
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {messages.filter(m => m.role === 'assistant').slice(-1)[0]?.content || 'Waiting...'}
                </p>
              </div>
            </div>
          </div>

          {/* Photo Comparison Section - Show photos for Part 2 exercises */}
          {currentExercise.content?.task_type === 'photo_comparison' && currentExercise.content?.photos && (
            <div className="py-4 bg-white border-b border-slate-200">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-slate-700 mb-1">📷 Fotografías para comparar</h3>
                <p className="text-xs text-slate-500">{currentExercise.content.part_description_es || 'Compara estas dos fotografías'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentExercise.content.photos.map((photo, index) => (
                  <div key={photo.id} className="relative group">
                    <div className="aspect-video overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                      <img 
                        src={photo.url} 
                        alt={photo.description}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="mt-2 px-1">
                      <span className="text-xs font-medium text-orange-600">Foto {index + 1}</span>
                      <p className="text-xs text-slate-600 mt-0.5">{photo.description_es || photo.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Discussion Options Section - Show image or options for Part 3 exercises */}
          {currentExercise.content?.task_type === 'collaborative_discussion' && (currentExercise.content?.discussion_image || currentExercise.content?.discussion_options) && (
            <div className="py-4 bg-blue-50 border-b border-blue-100">
              {currentExercise.content.discussion_image ? (
                <div className="flex justify-center">
                  <img 
                    src={currentExercise.content.discussion_image} 
                    alt="Discussion prompt" 
                    className="max-w-full max-h-[400px] object-contain rounded"
                    data-testid="discussion-image"
                  />
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">Opciones para discutir</h3>
                    <p className="text-xs text-blue-600">{currentExercise.content.part_description_es || 'Discute estas opciones'}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentExercise.content.discussion_options.map((option, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-white border border-blue-200 text-blue-800"
                      >
                        <span className="w-5 h-5 flex items-center justify-center bg-blue-100 rounded-full text-xs font-medium mr-2">
                          {index + 1}
                        </span>
                        {currentExercise.content.discussion_options_es?.[index] || option}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Chat Area */}
          <div className="flex-1 overflow-hidden py-4">
            <ScrollArea className="h-full" ref={scrollRef}>
              <div className="space-y-4 pb-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 animate-fade-in-up ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-sm ${
                        message.role === 'user'
                          ? 'bg-orange-600 text-white'
                          : 'bg-white border border-slate-200 text-slate-700'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-orange-600" />
                      </div>
                    )}
                  </div>
                ))}
                
                {sending && (
                  <div className="flex gap-3 justify-start animate-fade-in">
                    <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white border border-slate-200 p-4 rounded-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Session Ended - Feedback Card */}
          {sessionEnded && (
            <div className="py-4 bg-green-50 border-t border-green-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-green-800">Part {currentExercise.exam_part || 1} Completada</h3>
                  </div>
                  {finalFeedback && (
                    <p className="text-sm text-green-700">{finalFeedback}</p>
                  )}
                </div>
              </div>
              {/* Overall Feedback Button - Only show on Part 4 */}
              {currentExercise.exam_part === 4 && (
                <div className="mt-4 text-center">
                  <Button
                    onClick={handleGetOverallFeedback}
                    disabled={loadingOverallFeedback}
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6"
                  >
                    {loadingOverallFeedback ? 'Generando...' : 'Overall Feedback of all 4 parts'}
                  </Button>
                </div>
              )}
              {/* Overall Feedback Display */}
              {overallFeedback && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Overall Feedback - Sample Test {currentExercise.sample_test}</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{overallFeedback}</p>
                  {overallScore !== null && (
                    <div className="mt-3 text-center">
                      <span className="bg-green-200 text-green-800 px-4 py-2 rounded-full font-bold">
                        Overall Score: {overallScore}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Vocabulary Hints */}
          {!sessionEnded && currentExercise.content?.vocabulary_hints && (
            <div className="py-3 bg-orange-50 border-t border-orange-100">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-orange-700 mb-1">Frases útiles:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentExercise.content.vocabulary_hints.map((hint, i) => (
                      <span 
                        key={i}
                        className="text-xs bg-white px-2 py-1 rounded-sm text-slate-600 border border-orange-200 cursor-pointer hover:bg-orange-100"
                        onClick={() => setInput(prev => prev + (prev ? ' ' : '') + hint)}
                      >
                        {hint}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Input Area - Voice Only */}
          {!sessionEnded && (
            <div className="py-4 bg-white border-t border-slate-200">
              <div className="flex flex-col items-center gap-4">
                <Button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                  className={`w-20 h-20 rounded-full transition-all ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-400 animate-pulse' 
                      : 'bg-orange-600 hover:bg-orange-500'
                  }`}
                  data-testid="record-btn"
                >
                  {isTranscribing ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-8 h-8" />
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </Button>
                <p className="text-sm text-slate-500">
                  {isTranscribing 
                    ? 'Transcribiendo...' 
                    : isRecording 
                      ? 'Haz clic para detener' 
                      : 'Haz clic para grabar tu respuesta'}
                </p>
                
                {input && (
                  <div className="w-full flex gap-3">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Texto transcrito..."
                      className="flex-1 h-12 bg-slate-50 border-slate-200"
                      data-testid="transcribed-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!input.trim() || sending}
                      className="h-12 px-6 bg-slate-900 hover:bg-slate-800 rounded-full"
                      data-testid="send-transcribed-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
